import React, { useEffect, useRef, useState } from "react";
import { Dialog } from "@mui/material";
import { Editor as TinyEditor } from "@tinymce/tinymce-react";
import { toast } from "react-toastify";
import { Icon } from "@iconify/react";
import {
  ButtonPrimary,
  ButtonSecondary,
  HStack,
  VStack,
} from "../../../components/utils";
import { Input } from "../../../components/shared/InputField";
import {
  fixEmailStyleApi,
  getSystemVariables,
  polishEmailMessage,
  SystemVariable,
} from "../../../api/customEmail";
import { BODY_START_MARKER, BODY_END_MARKER } from "./helpers";

interface Props {
  htmlContent: string;
  subject: string;
  onSubjectChange: (s: string) => void;
  onNext: (html: string) => void;
  onBack: () => void;
}

type VarEntry = {
  variable: string;
  label: string;
  description: string;
  model: string;
};

const MODEL_LABEL: Record<string, string> = {
  employee: "Employee",
  employeeMetaData: "Employee Details",
  company: "Company",
  companyMetaData: "Company Details",
  system: "System",
};

const MODEL_COLORS: Record<string, { bg: string; text: string; dot: string }> =
  {
    employee: {
      bg: "bg-violet-50",
      text: "text-violet-700",
      dot: "bg-violet-400",
    },
    employeeMetaData: {
      bg: "bg-blue-50",
      text: "text-blue-700",
      dot: "bg-blue-400",
    },
    company: {
      bg: "bg-emerald-50",
      text: "text-emerald-700",
      dot: "bg-emerald-400",
    },
    companyMetaData: {
      bg: "bg-teal-50",
      text: "text-teal-700",
      dot: "bg-teal-400",
    },
    system: {
      bg: "bg-orange-50",
      text: "text-orange-700",
      dot: "bg-orange-400",
    },
  };

export default function Step2Edit({
  htmlContent,
  subject,
  onSubjectChange,
  onNext,
  onBack,
}: Props) {
  const editorRef = useRef<TinyEditor["editor"]>();

  const [templateParts, setTemplateParts] = useState<{
    header: string;
    body: string;
    footer: string;
  }>({ header: "", body: htmlContent, footer: "" });

  const [systemVars, setSystemVars] = useState<SystemVariable[]>([]);
  const [varFilter, setVarFilter] = useState("");
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({
    employee: true,
  });

  const [aiLoading, setAiLoading] = useState<"polish" | "style" | null>(null);
  const [aiReview, setAiReview] = useState<{
    original: string;
    improved: string;
    label: string;
    isSelection: boolean;
  } | null>(null);
  const [styleDialog, setStyleDialog] = useState(false);
  const [stylePrompt, setStylePrompt] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    getSystemVariables()
      .then(setSystemVars)
      .catch(() => {});
  }, []);

  useEffect(() => {
    const startIdx = htmlContent.indexOf(BODY_START_MARKER);
    const endIdx = htmlContent.indexOf(BODY_END_MARKER);
    if (startIdx !== -1 && endIdx !== -1) {
      setTemplateParts({
        header: htmlContent.substring(0, startIdx),
        body: htmlContent.substring(
          startIdx + BODY_START_MARKER.length,
          endIdx
        ),
        footer: htmlContent.substring(endIdx + BODY_END_MARKER.length),
      });
    } else {
      setTemplateParts({ header: "", body: htmlContent, footer: "" });
    }
  }, [htmlContent]);

  const insertVariable = (variable: string) => {
    editorRef.current?.insertContent(variable);
    editorRef.current?.focus();
  };

  const handleNext = () => {
    if (!subject.trim()) {
      toast.error("Please enter an email subject");
      return;
    }
    const editedBody = editorRef.current?.getContent() ?? templateParts.body;

    // Quick client-side check: flag dot-notation variables not in the known system var list
    const knownVars = new Set(systemVars.map((v) => v.variable));
    const allVars = editedBody.match(/#[a-zA-Z][a-zA-Z0-9_.]*#/g) ?? [];
    const suspicious = [...new Set(allVars)].filter(
      (v) => v.includes(".") && !knownVars.has(v)
    );
    if (suspicious.length > 0) {
      toast.warning(
        `Unknown variable${
          suspicious.length > 1 ? "s" : ""
        } detected: ${suspicious.join(
          ", "
        )} — these will NOT be replaced. Fix them before sending.`,
        { autoClose: 6000 }
      );
      // Still allow proceeding — Step 3 will hard-block with full details
    }

    onNext(
      templateParts.header +
        BODY_START_MARKER +
        editedBody +
        BODY_END_MARKER +
        templateParts.footer
    );
  };

  const handlePolish = async () => {
    const body = editorRef.current?.getContent();
    if (!body?.trim()) {
      toast.error("Editor is empty");
      return;
    }
    setAiLoading("polish");
    try {
      const improved = await polishEmailMessage(body);
      if (improved)
        setAiReview({
          original: body,
          improved,
          label: "Polish Message",
          isSelection: false,
        });
    } catch {
      toast.error("AI request failed. Please try again.");
    } finally {
      setAiLoading(null);
    }
  };

  const handleStyleSubmit = async () => {
    const selection = editorRef.current?.selection.getContent({
      format: "html",
    });
    const body = editorRef.current?.getContent();
    const target = selection?.trim() ? selection : body;
    const isSelection = !!selection?.trim();
    if (!target?.trim()) {
      toast.error("Editor is empty");
      return;
    }
    setAiLoading("style");
    setStyleDialog(false);
    try {
      const improved = await fixEmailStyleApi(
        target,
        stylePrompt.trim() || undefined
      );
      if (improved) {
        setAiReview({
          original: target,
          improved,
          label: isSelection ? "Fix Style (selection)" : "Fix Style",
          isSelection,
        });
      }
    } catch {
      toast.error("AI request failed. Please try again.");
    } finally {
      setAiLoading(null);
      setStylePrompt("");
    }
  };

  const handleAIAccept = () => {
    if (!aiReview) return;
    if (aiReview.isSelection) {
      editorRef.current?.selection.setContent(aiReview.improved);
    } else {
      editorRef.current?.setContent(aiReview.improved);
    }
    toast.success(`${aiReview.label} applied!`);
    setAiReview(null);
  };

  const previewDocHtml = (() => {
    const editedBody = editorRef.current?.getContent() ?? templateParts.body;
    const fullHtml =
      templateParts.header +
      BODY_START_MARKER +
      editedBody +
      BODY_END_MARKER +
      templateParts.footer;
    // If header is missing (no wrapper), the preview would show body-only without
    // Hissa branding. Add a minimal full-page background so it at least looks intentional.
    const hasWrapper = templateParts.header.trim().length > 0;
    const doc = hasWrapper
      ? fullHtml
      : `<!DOCTYPE html><html><head><meta charset="UTF-8">
<style>body{margin:0;padding:20px;background:#E5E5E5;font-family:sans-serif;}
.inner{max-width:600px;margin:0 auto;background:#fff;padding:24px;}</style>
</head><body><div class="inner">${editedBody}</div></body></html>`;
    return doc.split("#today#").join(new Date().toLocaleDateString("en-IN"));
  })();

  // Build grouped/filtered variable list
  const varsByModel = (systemVars as VarEntry[]).reduce<
    Record<string, VarEntry[]>
  >((acc, v) => {
    const key = v.model || "system";
    if (!acc[key]) acc[key] = [];
    acc[key].push(v);
    return acc;
  }, {});

  const filteredVarsByModel = Object.entries(varsByModel).reduce<
    Record<string, VarEntry[]>
  >((acc, [model, vars]) => {
    const filtered = vars.filter(
      (v) =>
        !varFilter ||
        v.label.toLowerCase().includes(varFilter.toLowerCase()) ||
        v.variable.toLowerCase().includes(varFilter.toLowerCase())
    );
    if (filtered.length > 0) acc[model] = filtered;
    return acc;
  }, {});

  return (
    <HStack className="gap-0" style={{ minHeight: "calc(100vh - 220px)" }}>
      {/* Variable panel */}
      <VStack
        className="w-56 min-w-[14rem] border-r border-gray-100 bg-white overflow-y-auto shrink-0"
        style={{
          boxShadow: "inset -1px 0 0 #f0f0f0",
          height: "calc(100vh - 220px)",
        }}
      >
        <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 shrink-0">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">
            Variables
          </p>
          <div className="relative">
            <Icon
              icon="mdi:magnify"
              className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400"
              height={14}
            />
            <input
              type="text"
              className="w-full border border-gray-200 rounded-md pl-6 pr-2 py-1.5 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-orange-400 focus:border-orange-400"
              placeholder="Search variables..."
              value={varFilter}
              onChange={(e) => setVarFilter(e.target.value)}
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto py-2">
          {systemVars.length === 0 && (
            <p className="text-xs text-gray-400 italic px-4 py-3">Loading…</p>
          )}
          {Object.entries(filteredVarsByModel).map(([model, vars]) => {
            const colors = MODEL_COLORS[model] ?? {
              bg: "bg-gray-50",
              text: "text-gray-600",
              dot: "bg-gray-400",
            };
            const isOpen = !!openGroups[model];
            return (
              <div
                key={model}
                className="border-b border-gray-100 last:border-b-0"
              >
                <button
                  type="button"
                  onClick={() =>
                    setOpenGroups((prev) => ({
                      ...prev,
                      [model]: !prev[model],
                    }))
                  }
                  className="flex items-center justify-between w-full px-4 py-2.5 hover:bg-gray-50 transition-colors text-left"
                >
                  <HStack className="items-center gap-2">
                    <span
                      className={`w-2 h-2 rounded-full shrink-0 ${colors.dot}`}
                    />
                    <span className="text-xs font-semibold text-gray-600">
                      {MODEL_LABEL[model] ?? model}
                    </span>
                    <span className="text-xs text-gray-400">
                      ({vars.length})
                    </span>
                  </HStack>
                  <Icon
                    icon={isOpen ? "mdi:chevron-up" : "mdi:chevron-down"}
                    height={16}
                    className="text-gray-400 shrink-0"
                  />
                </button>
                {isOpen && (
                  <div className="px-3 pb-2 flex flex-col gap-0.5">
                    {vars.map((v) => (
                      <button
                        key={v.variable}
                        type="button"
                        onClick={() => insertVariable(v.variable)}
                        title={v.variable}
                        className={`group flex items-center justify-between w-full text-left rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors ${colors.text} hover:${colors.bg}`}
                      >
                        <span className="truncate">{v.label}</span>
                        <Icon
                          icon="mdi:plus-circle-outline"
                          height={13}
                          className="shrink-0 ml-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="px-4 py-2 border-t border-gray-100 bg-gray-50 shrink-0">
          <p className="text-xs text-gray-400">Click to insert at cursor</p>
        </div>
      </VStack>

      {/* Editor panel */}
      <VStack className="flex-1 gap-0 overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-100 bg-white shrink-0">
          <HStack className="items-center gap-3">
            <span className="text-sm font-medium text-gray-600 whitespace-nowrap w-14">
              Subject
            </span>
            <Input
              className="flex-1 px-3 py-1.5 text-sm border-gray-200 rounded-md"
              value={subject}
              onChange={(e) => onSubjectChange(e.target.value)}
              placeholder="Enter email subject..."
            />
          </HStack>
        </div>

        <div className="relative">
          {aiLoading && (
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white/70 backdrop-blur-[1px]">
              <Icon
                icon="mdi:loading"
                className="text-violet-600 animate-spin"
                height={32}
              />
              <p className="mt-2 text-sm font-medium text-violet-700">
                {aiLoading === "polish"
                  ? "Polishing message…"
                  : "Applying style…"}
              </p>
            </div>
          )}
          <TinyEditor
            apiKey="l8zomkw48zb8qhvwpahb2g5nodmnma6qjyz1acf2y14t53z6"
            onInit={(_evt, editor) => {
              editorRef.current = editor;
            }}
            initialValue={templateParts.body}
            init={{
              height: "calc(100vh - 330px)",
              menubar: false,
              plugins: "lists link code",
              toolbar:
                "undo redo | formatselect | bold italic | forecolor | alignleft aligncenter alignright | bullist numlist | link | code",
              content_style:
                "body { font-family:Helvetica,Arial,sans-serif; font-size:14px; padding:12px; }",
              resize: false,
            }}
          />
        </div>

        <div className="px-5 py-3 border-t border-gray-100 bg-gray-50 shrink-0">
          <HStack className="items-center justify-between">
            <p className="text-xs text-gray-400">
              Use{" "}
              <code className="bg-gray-200 px-1 py-0.5 rounded text-gray-600">
                #customVariable#
              </code>{" "}
              for per-employee custom values (filled via Excel in Step 4).
            </p>
            <HStack className="gap-2">
              <ButtonSecondary onClick={onBack}>Back</ButtonSecondary>
            </HStack>
            <HStack className="gap-2">
              <ButtonSecondary
                onClick={handlePolish}
                loading={aiLoading === "polish"}
                disabled={aiLoading !== null}
                className="flex items-center gap-1"
              >
                <Icon icon="mdi:auto-fix" height={15} />
                Polish Message
              </ButtonSecondary>
              <ButtonSecondary
                onClick={() => setStyleDialog(true)}
                loading={aiLoading === "style"}
                disabled={aiLoading !== null}
                className="flex items-center gap-1"
              >
                <Icon icon="mdi:palette-outline" height={15} />
                Fix Style
              </ButtonSecondary>
              <ButtonSecondary
                onClick={() => setPreviewOpen(true)}
                className="flex items-center gap-1"
              >
                <Icon icon="mdi:eye-outline" height={15} />
                Preview
              </ButtonSecondary>
              <ButtonPrimary onClick={handleNext}>Continue</ButtonPrimary>
            </HStack>
          </HStack>
        </div>
      </VStack>

      {/* Preview dialog */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
          <span className="text-sm font-semibold text-gray-700">
            Template Preview
          </span>
          <button
            type="button"
            onClick={() => setPreviewOpen(false)}
            className="text-gray-400 hover:text-gray-600"
          >
            <Icon icon="mdi:close" height={20} />
          </button>
        </div>
        <div
          className="p-0"
          style={{ height: "70vh", backgroundColor: "#E5E5E5" }}
        >
          <iframe
            title="Template Preview"
            srcDoc={previewDocHtml}
            style={{
              width: "100%",
              height: "100%",
              border: "none",
              display: "block",
            }}
            sandbox="allow-same-origin"
          />
        </div>
      </Dialog>

      {/* Style prompt dialog */}
      <Dialog
        open={styleDialog}
        onClose={() => setStyleDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
          <div>
            <span className="text-sm font-semibold text-gray-700">
              Fix Style with AI
            </span>
            <span className="ml-2 text-xs text-gray-400">
              Select text first to style just that portion
            </span>
          </div>
          <button
            type="button"
            onClick={() => setStyleDialog(false)}
            className="text-gray-400 hover:text-gray-600"
          >
            <Icon icon="mdi:close" height={20} />
          </button>
        </div>
        <div className="px-5 py-4 flex flex-col gap-3">
          <p className="text-xs text-gray-500">
            Describe how you want the styling changed. Leave blank for a general
            professional improvement.
          </p>
          <textarea
            className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-violet-400 resize-none"
            rows={4}
            placeholder={`e.g. "Make the heading bold and dark blue"\n"Make it look like a formal letter"\n"Add a highlight box around key dates"`}
            value={stylePrompt}
            onChange={(e) => setStylePrompt(e.target.value)}
            autoFocus
          />
        </div>
        <div className="flex items-center justify-between gap-3 px-5 py-3 border-t border-gray-100 bg-gray-50">
          <ButtonSecondary onClick={() => setStyleDialog(false)}>
            Cancel
          </ButtonSecondary>
          <ButtonPrimary
            onClick={handleStyleSubmit}
            loading={aiLoading === "style"}
          >
            <Icon icon="mdi:palette-outline" height={15} className="mr-1" />
            Apply Style
          </ButtonPrimary>
        </div>
      </Dialog>

      {/* AI Review dialog — side-by-side diff */}
      <Dialog
        open={!!aiReview}
        onClose={() => setAiReview(null)}
        maxWidth="xl"
        fullWidth
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
          <div>
            <span className="text-sm font-semibold text-gray-700">
              AI Suggestion — {aiReview?.label}
            </span>
            <span className="ml-2 text-xs text-gray-400">
              {aiReview?.isSelection
                ? "Applies to selected text only"
                : "Review the changes before applying"}
            </span>
          </div>
          <button
            type="button"
            onClick={() => setAiReview(null)}
            className="text-gray-400 hover:text-gray-600"
          >
            <Icon icon="mdi:close" height={20} />
          </button>
        </div>

        <div
          className="grid grid-cols-2 divide-x divide-gray-100"
          style={{ height: "65vh" }}
        >
          <div className="flex flex-col overflow-hidden">
            <div className="px-4 py-2 bg-gray-50 border-b border-gray-100 shrink-0">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Original
              </span>
            </div>
            <iframe
              title="Original"
              srcDoc={
                aiReview?.isSelection
                  ? `<body style="font-family:Helvetica,Arial,sans-serif;font-size:14px;padding:16px;">${
                      aiReview?.original ?? ""
                    }</body>`
                  : templateParts.header +
                    BODY_START_MARKER +
                    (aiReview?.original ?? "") +
                    BODY_END_MARKER +
                    templateParts.footer
              }
              className="flex-1 w-full bg-white"
              sandbox="allow-same-origin"
            />
          </div>
          <div className="flex flex-col overflow-hidden">
            <div className="px-4 py-2 bg-violet-50 border-b border-violet-100 shrink-0">
              <span className="text-xs font-semibold text-violet-600 uppercase tracking-wide">
                AI Improved
              </span>
            </div>
            <iframe
              title="AI Result"
              srcDoc={
                aiReview?.isSelection
                  ? `<body style="font-family:Helvetica,Arial,sans-serif;font-size:14px;padding:16px;">${
                      aiReview?.improved ?? ""
                    }</body>`
                  : templateParts.header +
                    BODY_START_MARKER +
                    (aiReview?.improved ?? "") +
                    BODY_END_MARKER +
                    templateParts.footer
              }
              className="flex-1 w-full bg-white"
              sandbox="allow-same-origin"
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-5 py-3 border-t border-gray-100 bg-gray-50">
          <ButtonSecondary onClick={() => setAiReview(null)}>
            Discard
          </ButtonSecondary>
          <ButtonPrimary onClick={handleAIAccept}>
            Apply AI Changes
          </ButtonPrimary>
        </div>
      </Dialog>
    </HStack>
  );
}
